/**
 * ImageSelectionGrid - DENiZEN grid for selecting from 4 generated images.
 *
 * Design: DENiZEN
 * - Soft shadows and rounded corners in a 2x2 grid
 * - Muted palette with mid-green selection state
 * - WCAG 2.1 AA compliant with keyboard navigation
 */

import { useState } from 'react'
import type { ImageOption } from '@/services/api'
import { ImageLightbox } from './ImageLightbox'

export interface ImageSelectionGridProps {
  /**
   * Array of image options to display (up to 4)
   */
  images: ImageOption[]
  /**
   * Callback when user confirms their selection
   */
  onSelect: (selectedImage: ImageOption) => void
  /**
   * Whether selection is currently being processed
   */
  isLoading?: boolean
  /**
   * Whether images are being generated (shows skeleton placeholders)
   */
  isGenerating?: boolean
}

export function ImageSelectionGrid({
  images,
  onSelect,
  isLoading = false,
  isGenerating = false,
}: ImageSelectionGridProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [previewImage, setPreviewImage] = useState<ImageOption | null>(null)

  const handleImageClick = (index: number) => {
    if (!isLoading) {
      setSelectedIndex(index)
    }
  }

  const handleConfirm = () => {
    if (selectedIndex !== null && images[selectedIndex]) {
      onSelect(images[selectedIndex])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleImageClick(index)
    }
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6 text-center">
        <h2
          className="text-2xl font-display font-bold text-dark-green tracking-tight mb-2"
          style={{ letterSpacing: '-0.02em' }}
        >
          {isGenerating ? 'Creating Your Visions' : 'Choose Your Vision'}
        </h2>
        <p className="text-muted font-body">
          {isGenerating
            ? 'AI is generating 4 unique images based on your conversation...'
            : 'Select the image that best represents your community\'s future'}
        </p>
        {isGenerating && (
          <p className="text-muted font-body text-sm mt-2">
            This usually takes 30-60 seconds
          </p>
        )}
      </div>

      {/* 2x2 Grid */}
      <div
        className="grid grid-cols-2 gap-4 mb-6"
        role={isGenerating ? undefined : 'radiogroup'}
        aria-label={isGenerating ? 'Images being generated' : 'Image selection grid'}
      >
        {isGenerating ? (
          // Skeleton placeholders during generation
          Array.from({ length: 4 }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="relative aspect-square overflow-hidden border border-dark-green/15 bg-stone rounded-2xl shadow-denizen-sm"
            >
              {/* Animated shimmer effect */}
              <div
                className="absolute inset-0 animate-pulse"
                style={{
                  background: 'linear-gradient(90deg, #fff8eb 0%, #f0ead8 50%, #fff8eb 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 1.5s ease-in-out infinite',
                }}
              />

              {/* Center icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 border border-dark-green/15 rounded-full flex items-center justify-center bg-white shadow-denizen-sm">
                  <svg
                    className="w-8 h-8 text-muted animate-spin"
                    style={{ animationDuration: '2s' }}
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="3"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              </div>

              {/* Number badge */}
              <div className="absolute top-3 left-3 w-10 h-10 rounded-full border border-dark-green/15 flex items-center justify-center font-display font-bold text-lg bg-white shadow-denizen-sm">
                {index + 1}
              </div>

              {/* Status label */}
              <div className="absolute bottom-3 left-3 right-3 px-3 py-2 border border-dark-green/15 text-center rounded-2xl bg-white">
                <span className="text-xs font-display font-bold text-muted">
                  Generating...
                </span>
              </div>
            </div>
          ))
        ) : (
          // Actual images when available
          <>
            {images.map((image, index) => {
              const isSelected = selectedIndex === index
              const isHovered = hoveredIndex === index

              return (
                <div
                  key={image.image_id}
                  className={`
                    group relative aspect-square
                    transition-transform duration-200
                    ${isSelected ? 'scale-[0.98]' : ''}
                    ${isHovered && !isSelected ? '-translate-y-1' : ''}
                    animate-fade-in
                  `}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={`Vision option ${index + 1}`}
                    tabIndex={isLoading ? -1 : 0}
                    onClick={() => handleImageClick(index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className={`
                      absolute inset-0 overflow-hidden
                      border border-dark-green/15 rounded-2xl
                      transition-shadow duration-200
                      focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green
                      ${isLoading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                    `}
                    style={{
                      boxShadow: isSelected
                        ? 'inset 0 0 0 4px var(--color-mid-green, #40aa3b), var(--shadow-denizen)'
                        : isHovered
                          ? 'var(--shadow-denizen)'
                          : 'var(--shadow-denizen-sm)',
                    }}
                  >
                    {/* Image */}
                    <img
                      src={image.image_url}
                      alt={`Generated vision option ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />

                    {/* Selection overlay */}
                    {isSelected && (
                      <div
                        className="absolute inset-0 flex items-center justify-center bg-mid-green/20"
                      >
                        <div className="w-16 h-16 border border-dark-green/15 rounded-full flex items-center justify-center bg-mid-green shadow-denizen-sm">
                          <svg
                            className="w-8 h-8 text-dark-green"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      </div>
                    )}

                    {/* Number badge */}
                    <div
                      className="absolute top-3 left-3 w-10 h-10 rounded-full border border-dark-green/15 flex items-center justify-center font-display font-bold text-lg shadow-denizen-sm"
                      style={{
                        backgroundColor: isSelected ? 'var(--color-mid-green, #40aa3b)' : '#FFFFFF',
                      }}
                    >
                      {index + 1}
                    </div>
                  </div>

                  {/* Fullscreen preview — sibling of radio so the two interactives are not nested.
                      Real <button> for keyboard access (WCAG 2.1.1). */}
                  <button
                    type="button"
                    onClick={() => setPreviewImage(image)}
                    disabled={isLoading}
                    aria-label={`View image ${index + 1} fullscreen`}
                    className={`
                      absolute top-3 right-3 w-10 h-10 border border-dark-green/15 rounded-full
                      flex items-center justify-center bg-white z-10
                      transition-all duration-200
                      hover:scale-110 hover:-translate-y-0.5
                      opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100
                      shadow-denizen-sm
                      focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green
                      disabled:cursor-not-allowed disabled:opacity-50
                      ${isHovered || isSelected ? 'md:opacity-100' : ''}
                    `}
                  >
                    <svg
                      className="w-5 h-5 text-dark-green"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                      />
                    </svg>
                  </button>
                </div>
              )
            })}

            {/* Empty slots if fewer than 4 images */}
            {Array.from({ length: Math.max(0, 4 - images.length) }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="aspect-square border border-dark-green/15 border-dashed bg-stone rounded-2xl flex items-center justify-center"
              >
                <div className="text-muted font-body text-sm">
                  Not available
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Confirm Button */}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={selectedIndex === null || isLoading}
          className={`
            px-8 py-4 border border-dark-green/15 font-display font-bold text-lg rounded-2xl
            transition-all duration-200
            focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green
            ${
              selectedIndex !== null && !isLoading
                ? 'cursor-pointer hover:-translate-y-1 shadow-denizen bg-dark-green text-white'
                : 'cursor-not-allowed opacity-50 bg-stone text-muted shadow-denizen-sm'
            }
          `}
        >
          {isLoading ? (
            <span className="flex items-center gap-3">
              <svg
                className="w-5 h-5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Selecting...
            </span>
          ) : (
            'Choose This Vision'
          )}
        </button>
      </div>

      {/* Helper text */}
      <p className="text-center text-muted text-sm mt-4 font-body">
        Click an image to select it, or tap the magnifying glass to view fullscreen
      </p>

      {/* Fullscreen preview lightbox */}
      <ImageLightbox
        imageUrl={previewImage?.image_url ?? ''}
        alt={previewImage ? `Generated vision option fullscreen preview` : ''}
        isOpen={previewImage !== null}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  )
}

export default ImageSelectionGrid
